import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { camposAPI, partidasAPI } from '../services/api';
import { useSesion } from '../context/SesionContext';
import { handicapDeJuego, parDelCampo, parPersonal } from '../utils/handicap';
import './CreateGame.css';

function CreateGame() {
  const navigate = useNavigate();
  const { usuario, actualizarUsuario } = useSesion();
  const [campo, setCampo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    nombre: '',
    handicap: ''
  });

  useEffect(() => {
    loadCampo();
  }, []);

  // Quien ha entrado con su cuenta se encuentra el formulario relleno
  useEffect(() => {
    if (!usuario) return;
    setFormData(prev => ({
      nombre: prev.nombre || usuario.nombre,
      handicap: prev.handicap === '' ? String(usuario.handicap ?? '') : prev.handicap
    }));
  }, [usuario]);

  const loadCampo = async () => {
    try {
      const response = await camposAPI.getActual();
      setCampo(response.data);
    } catch (err) {
      console.error('Error al cargar el campo:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar el campo';
      setError(`${errorMsg}. Verifica tu conexión a internet.`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.nombre.trim()) {
      setError('Por favor, escribe tu nombre');
      return;
    }

    setCreating(true);

    try {
      const response = await partidasAPI.create({
        jugador: {
          nombre: formData.nombre.trim(),
          handicap: formData.handicap === '' ? 0 : Number(formData.handicap)
        }
      });

      // Guardar datos en localStorage
      localStorage.setItem('partidaCodigo', response.data.codigo);
      localStorage.setItem('jugadorId', response.data.jugadorId);
      localStorage.setItem('jugadorNombre', formData.nombre.trim());

      // El servidor ha guardado este handicap en la cuenta
      if (usuario) {
        actualizarUsuario({ handicap: formData.handicap === '' ? 0 : Number(formData.handicap) });
      }

      // Redirigir a sala de espera
      navigate(`/sala/${response.data.codigo}`);
    } catch (err) {
      console.error('Error al crear partida:', err);
      setError(err.response?.data?.error || 'Error al crear la partida. Inténtalo de nuevo.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#2e7d32' }}>Cargando campo...</p>
      </div>
    );
  }

  const par = campo ? parDelCampo(campo.hoyos) : 0;
  const ida = campo ? parDelCampo(campo.hoyos.slice(0, 9)) : 0;
  const vuelta = campo ? parDelCampo(campo.hoyos.slice(9)) : 0;
  const hcpJuego = handicapDeJuego(formData.handicap);
  const miPar = parPersonal(par, formData.handicap);

  return (
    <div className="create-game">
      <div className="header">
        <h1>Crear Nueva Partida</h1>
        <p>{campo?.nombre || 'Configura tu partida de golf'}</p>
      </div>

      <div className="container">
        <div className="card fade-in">
          {error && <div className="error">{error}</div>}

          {!campo ? (
            <div className="no-campos">
              <p>No se ha podido cargar el campo.</p>
              <button className="btn btn-primary" onClick={loadCampo}>
                Reintentar
              </button>
            </div>
          ) : (
            <>
              <div className="campo-fijo">
                <div className="campo-fijo-nombre">⛳ {campo.nombre}</div>
                <div className="campo-fijo-datos">
                  {campo.hoyos.length} hoyos · Par {par} (ida {ida} / vuelta {vuelta})
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="nombre">Tu Nombre</label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Ej: Juan Pérez"
                    required
                    maxLength="50"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="handicap">Tu Handicap</label>
                  <input
                    type="number"
                    id="handicap"
                    name="handicap"
                    value={formData.handicap}
                    onChange={handleChange}
                    placeholder="Ej: 18.5"
                    min="0"
                    max="54"
                    step="0.1"
                    required
                  />
                </div>

                {usuario && (
                  <p className="aviso-cuenta">
                    Esta partida se guardará en tu historial
                  </p>
                )}

                <div className="par-personal">
                  <div className="par-personal-label">Tu par en {campo.nombre}</div>
                  <div className="par-personal-valor">{miPar}</div>
                  <div className="par-personal-detalle">
                    Par {par} del campo + {hcpJuego} golpes de tu handicap, repartidos
                    hoyo a hoyo según su índice
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-full"
                  disabled={creating}
                >
                  {creating ? 'Creando...' : 'Crear Partida'}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary btn-full"
                  onClick={() => navigate('/')}
                  style={{ marginTop: '12px' }}
                >
                  Cancelar
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateGame;
